import type {
  CostCheckState,
  DictionaryValidationIndexEntry,
} from "@shared-core/data/cost-engine-validation-types";
import {
  costCheckStateLabel,
  costCheckStateTone,
} from "@shared-core/domain/cost-engine-validation-presentation";
import { Badge } from "../../primitives/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../primitives/tooltip";
import { ComparisonInline } from "../../patterns/ComparisonTriplet";

/**
 * The "Cost check" state badge — the same piece of meaning wherever a
 * dictionary item is listed (the dictionary index and the estate-wide Live
 * costing page). Kept as one shared component so the surfaces can never
 * silently drift in what a given state looks like.
 */
export function CostCheckCell({
  state,
  mismatchingCostLine,
  lastCheck,
}: {
  state: CostCheckState;
  mismatchingCostLine: { label: string } | null;
  lastCheck: DictionaryValidationIndexEntry["lastCheck"];
}) {
  const badge = (
    <Badge variant={costCheckStateTone(state)} className="cursor-help">
      {costCheckStateLabel(state)}
    </Badge>
  );
  return (
    <div className="flex items-center gap-1.5">
      <Tooltip>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        <TooltipContent>
          {lastCheck ? (
            <ComparisonInline
              inheritColor
              value={{
                kind: "money",
                ours: lastCheck.oursPence,
                tharstern: lastCheck.tharsternPence,
                // The badge's cost-check state is authoritative — never let
                // the check's own delta magnitude paint a different colour
                // than the badge it's a tooltip for.
                toneOverride: costCheckStateTone(state),
              }}
              label="Last check:"
            />
          ) : (
            "Not yet checked"
          )}
        </TooltipContent>
      </Tooltip>
      {state === "red" && mismatchingCostLine && (
        <span className="text-xs text-muted-foreground">
          {mismatchingCostLine.label}
        </span>
      )}
    </div>
  );
}
